import { forwardRef, RefObject, useState } from 'react';
import { useTranslations } from 'next-intl';
import { IProduct, IProductSectionName } from '@monofront/types';
import ProductReviewsSumup from '../ProductReviewsSumup';
import { IconSizes } from '@monofront/design-system/visuals/Icons/type';
import QuestionMarkOutline from '@monofront/design-system/visuals/Icons/QuestionMarkOutline';
import { useCanReviewProduct } from '@hooks/businessContainer';
import { useAuthentication } from '@context/businessContainer';
import GoToLoginPageButton from '@components/GoToLoginPageButton';
import PercentBar from '@monofront/design-system/components/PercentBar';
import InformativeBox from '@monofront/design-system/components/InformativeBox';
import CharacterSad from '@monofront/design-system/visuals/Illustrations/CharacterSad';
import ProductReviewsList from './ProductReviewsList';
import ProductReviewsPanel from './ProductReviewsPanel';
import { useProductPageContext } from '@context/ProductPageContext';
import { useEditorialSidePanelContext } from '@monofront/business/contexts';

interface IProductReviewsButtonProps {
  canReviewProduct: boolean;
  isAuthenticated: boolean;
  className?: string;
  onClick: () => void;
}

const ProductReviewsButton: React.FC<IProductReviewsButtonProps> = ({
  canReviewProduct,
  isAuthenticated,
  className = '',
  onClick,
}) => {
  const tReviews = useTranslations('Reviews');

  return canReviewProduct ? (
    <button
      type="button"
      className={`tw-lf-button tw-lf-button-primary tw-lf-button-md ${className}`}
      onClick={onClick}
    >
      {tReviews('giveReview')}
    </button>
  ) : !isAuthenticated ? (
    <GoToLoginPageButton
      label={tReviews('giveReview')}
      isButton
      UNSAFE_className={`tw-w-fit ${className}`}
    />
  ) : null;
};

interface IProductReviewsProps {
  product: IProduct;
  name: IProductSectionName;
  ref: RefObject<HTMLHeadingElement | null>;
}

const ProductReviews = forwardRef<HTMLHeadingElement, IProductReviewsProps>(
  ({ product, name }, ref) => {
    const tProducts = useTranslations('Products');
    const tReviews = useTranslations('Reviews');
    const tErrors = useTranslations('Errors');
    const { isAuthenticated } = useAuthentication();
    const { setEditorialPanelSourceUid } = useEditorialSidePanelContext();
    const { reviews_stats } = product.meta;
    const [isReviewPanelOpen, setIsReviewPanelOpen] = useState(false);
    const canReviewProduct = useCanReviewProduct(product.sku);
    const { useGetProductReviewsHook } = useProductPageContext();
    const { error } = useGetProductReviewsHook || {};

    if (!reviews_stats?.global?.count || reviews_stats.global.count <= 0) {
      return (
        <section>
          <h2
            ref={ref}
            id="section-reviews"
            className="section-title tw-lf-title-sm tw-mb-6 tw-mt-[4.5rem] desktop:tw-lf-title-md"
            data-menu={
              name.menuTitle
                ? name.menuTitle
                : tProducts('reviews', { product: product.title })
            }
            tabIndex={-1}
          >
            {name.sectionTitle
              ? name.sectionTitle.replace('%PRODUCT_NAME%', product.title)
              : tProducts('reviews', { product: product.title })}
          </h2>

          {canReviewProduct || !isAuthenticated ? (
            <p>{tReviews('giveFirstReview')}</p>
          ) : (
            <p>{tReviews('noReviewsYet')}</p>
          )}

          <ProductReviewsButton
            canReviewProduct={canReviewProduct}
            isAuthenticated={isAuthenticated}
            className="tw-mt-6"
            onClick={() => setIsReviewPanelOpen(true)}
          />

          <ProductReviewsPanel
            isOpen={isReviewPanelOpen}
            product={product}
            onSuccess={() => setIsReviewPanelOpen(false)}
            onCancel={() => setIsReviewPanelOpen(false)}
          />
        </section>
      );
    }

    return (
      <section>
        <h2
          ref={ref}
          id="section-reviews"
          className="tw-lf-title-sm tw-mb-6 tw-mt-[4.5rem] tw-scroll-mt-16 desktop:tw-lf-title-md"
          data-menu={
            name.menuTitle
              ? name.menuTitle
              : tProducts('reviews', { product: product.title })
          }
          tabIndex={-1}
        >
          {name.sectionTitle
            ? name.sectionTitle.replace('%PRODUCT_NAME%', product.title)
            : tProducts('reviews', { product: product.title })}
        </h2>

        {reviews_stats && (
          <ProductReviewsSumup
            reviewsStats={reviews_stats}
            starSize={IconSizes.lg}
          />
        )}

        <button
          type="button"
          className={`tw-lf-link-sm tw-lf-link-dark tw-mb-8 tw-mt-4`}
          onClick={() => setEditorialPanelSourceUid('reviews-explanations')}
        >
          <QuestionMarkOutline
            aria-hidden
            UNSAFE_className="tw-mr-2 tw-align-sub tw-size-4"
          />
          {tReviews('reviewsExplanation', { product: product.title })}
        </button>

        <ProductReviewsButton
          canReviewProduct={canReviewProduct}
          isAuthenticated={isAuthenticated}
          className="min-[1080px]:tw-float-right min-[1080px]:tw--mt-[32px]"
          onClick={() => setIsReviewPanelOpen(true)}
        />

        <ul className="tw-mt-8 min-[1080px]:tw-mt-0">
          {reviews_stats &&
            reviews_stats.per_rating?.length &&
            [...reviews_stats.per_rating]
              .sort((a, b) => b.rating_value - a.rating_value)
              .map(({ rating_value, rating_rate }, index) => (
                <li className="tw-mb-4 tw-flex tw-items-center" key={index}>
                  <span className="tw-mr-4 tw-min-w-[75px]">
                    {tReviews('statsPerRating', { stars: rating_value })}
                  </span>
                  <PercentBar
                    value={Number(rating_rate)}
                    width="164px"
                    UNSAFE_className="tw-w-full"
                  />
                </li>
              ))}
        </ul>

        {error ? (
          <InformativeBox
            hasPadding={false}
            hasImageOnMobile
            backgroundColor="transparent"
            icon={<CharacterSad aria-hidden="true" />}
            UNSAFE_className={`tw-mt-8`}
          >
            <p className="tw-py-3 tw-text-sm">{tErrors('reviewsFetchError')}</p>
          </InformativeBox>
        ) : (
          <ProductReviewsList />
        )}

        <ProductReviewsPanel
          isOpen={isReviewPanelOpen}
          product={product}
          onSuccess={() => setIsReviewPanelOpen(false)}
          onCancel={() => setIsReviewPanelOpen(false)}
        />
      </section>
    );
  },
);

export default ProductReviews;
